import {request} from "./BaseApi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {AcceptedTermsModel, AgreementModel} from "Apis/Models/AgreementModel";
import { AgreementTermsModel } from "Apis/Models/AgreementTermsModel";
import { SignContractModel } from "Apis/Models/SignContractModel";
import { AgreementEstimateModel } from "Apis/Models/AgreementEstimateModel";
import { saveAs } from 'file-saver';
import {ISendAgreementDto} from "./Models/SendAgreementDto";
import { MutateAgreementInfoModel } from "./Models/MutateAgreementInfoModel";
import moment from "moment";

export const AGREEMENTS_CACHE_KEY = 'AGREEMENTS_CACHE_KEY';
export const useAgreements = (onlySigned: boolean = false) => {
    return useQuery<AgreementModel[]>([AGREEMENTS_CACHE_KEY, onlySigned], async () => {
        return await request<AgreementModel[]>({
            url: `/agreement?onlySigned=${onlySigned}`,
            method: 'GET',
        })
    });
}

export const AGREEMENT_CACHE_KEY = 'AGREEMENT_CACHE_KEY';
export const useAgreement = (agreementId: string | undefined) => {

    return useQuery<AgreementModel, any>([AGREEMENT_CACHE_KEY, agreementId], async () => {
        return await request<AgreementModel>({
            url: `/agreement/${agreementId}`,
            method: 'GET',
        })
    }, {
        refetchOnWindowFocus: false,
        enabled: !!agreementId
    });
}

export const AGREEMENT_ESTIMATE_CACHE_KEY = 'AGREEMENT_ESTIMATE_CACHE_KEY';
export const useAgreementEstimate = (agreementId: string | undefined, fromImported: boolean = false) => {

    return useQuery<AgreementEstimateModel>([AGREEMENT_ESTIMATE_CACHE_KEY, agreementId], async () => {
        return await request<AgreementEstimateModel>({
            url: `/agreement/${agreementId}/estimate?fromImported=${fromImported}`,
            method: 'GET',
        })
    }, {
        refetchOnWindowFocus: false,
        enabled: !!agreementId
    });
}

export const useSignContract = () => {
    return useMutation(async (model: SignContractModel) => {
        return await request<AgreementModel>({
            url: `/agreement/${model.agreementId}/sign`,
            method: 'PUT',
            data: model
        })
    });
}

export const useSendContract = () => {
    return useMutation(async (model: ISendAgreementDto) => {
        return await request({
            url: `/agreement/${model.agreementId}/sendAgreement`,
            method: 'POST',
            data: model
        })
    });
}


export const AGREEMENT_TERMS_CACHE_KEY = 'AGREEMENT_TERMS_CACHE_KEY';
export const useAgreementTerms = () => {

    return useQuery<AgreementTermsModel[]>([AGREEMENT_TERMS_CACHE_KEY], async () => {
        return await request<AgreementTermsModel[]>({
            url: `/agreementTerms/current/`,
            method: 'GET',
        })
    }, {
        refetchOnWindowFocus: false
    });
}

export const CURRENT_AGREEMENT_TERMS_CACHE_KEY = 'CURRENT_AGREEMENT_TERMS_CACHE_KEY';
export const useCurrentAgreementTerms = (agreementId) => {

    return useQuery<AgreementTermsModel>([CURRENT_AGREEMENT_TERMS_CACHE_KEY, agreementId], async () => {
        return await request<AgreementTermsModel>({
            url: `/agreementTerms/current/${agreementId}`,
            method: 'GET',
        })
    }, {
        refetchOnWindowFocus: false
    });
}

export const useUpdateAgreement = () => {
    const queryClient = useQueryClient();

    return useMutation(async (agreement: AgreementModel): Promise<AgreementModel> => {
        return await request<AgreementModel>({
            url: `/agreement/${agreement.id}`,
            method: 'PUT',
            data: agreement
        })

    }, {
        onSuccess: () => {
            queryClient.invalidateQueries(AGREEMENT_CACHE_KEY);
            queryClient.invalidateQueries(AGREEMENTS_CACHE_KEY);
        }
    })
}

export const useUpdateAgreementV2 = () => {
    const queryClient = useQueryClient();

    return useMutation(async (agreement: MutateAgreementInfoModel): Promise<AgreementModel> => {
            return await request<AgreementModel>({
                url: `/agreement/${agreement.id}/v2`,
                method: 'PUT',
                data: agreement
            })

        },
        {
            onError: (error) => { /** do nothing */
            },
            onSuccess: () => {
                queryClient.invalidateQueries(AGREEMENT_CACHE_KEY);
                queryClient.invalidateQueries(AGREEMENTS_CACHE_KEY);
            }
        })
}

export const getAcceptedTermsPdf = async (agreement: AgreementModel, acceptedTerms: AcceptedTermsModel): Promise<any> => {

    try {
        const responseBlob = await request<Blob>({
            responseType: 'blob',
            url: `/agreement/${agreement.id}/acceptedTerms/${acceptedTerms.id}/pdf`,
            method: 'GET',
        });

        const blob = new Blob([responseBlob], {type: 'application/pdf' });

        let dateString = moment(acceptedTerms.validFrom).format('DD-MM-yyyy');

        if (!!acceptedTerms.validTo) {
            dateString = `${dateString} - ${moment(acceptedTerms.validTo).format('DD-MM-yyyy')}`
        }

        saveAs(blob, `The 0-Mission betingelser | ${agreement.companyName} | ${dateString}.pdf`);
    }
    catch (e) {
        return e;
    }
}
